import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, MapPin, Ship, Zap, Clock, Star, ChevronRight, Anchor, Map as MapIcon } from "lucide-react";
import { toast } from "sonner";
import PortsMap from "@/components/PortsMap";

const STATUS_LABEL = {
  open: { text: "Abierta", cls: "bg-[#EFE9E1] text-[#0B1D3A]" },
  accepted: { text: "Aceptada", cls: "bg-[#0B1D3A] text-white" },
  in_progress: { text: "En curso", cls: "bg-[#FF6B57] text-white" },
  completed: { text: "Completada", cls: "bg-emerald-600 text-white" },
  cancelled: { text: "Cancelada", cls: "bg-gray-300 text-gray-800" },
};

export default function OwnerDashboard() {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [reqs, setReqs] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [services, setServices] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [boats, setBoats] = useState([]);
  const [nearby, setNearby] = useState([]);
  const [open, setOpen] = useState(false);

  const [form, setForm] = useState({ service_type: "", boat_name: "", boat_size: "8-12m", province: "", comarca: "", port: "", preferred_date: "", notes: "", urgent: false, origin_port: "", origin_province: "", destination_port: "", destination_province: "" });
  const [priceHint, setPriceHint] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!form.service_type) { setPriceHint(null); return; }
    (async () => {
      try {
        const { data } = await api.post("/pricing/hint", {
          service_type: form.service_type,
          port: form.port || undefined,
          boat_size: form.boat_size || undefined,
        });
        if (!cancelled) setPriceHint(data);
      } catch (_) { if (!cancelled) setPriceHint(null); }
    })();
    return () => { cancelled = true; };
  }, [form.service_type, form.port, form.boat_size]);

  const load = async () => {
    try {
      const [r, n, b] = await Promise.all([api.get("/requests"), api.get("/notifications"), api.get("/boats")]);
      setReqs(r.data); setNotifs(n.data); setBoats(b.data);
      // Featured pros near owner's boats
      if (b.data.length > 0) {
        const port = b.data[0].home_port;
        const prov = b.data[0].home_province;
        const { data } = await api.get(`/professionals/promoted${port ? `?port=${encodeURIComponent(port)}` : prov ? `?province=${encodeURIComponent(prov)}` : ""}`);
        setNearby(data);
      }
    } catch (e) { toast.error(apiErr(e)); }
  };

  useEffect(() => {
    load();
    api.get("/services").then((r) => setServices(r.data));
    api.get("/geo/provinces").then((r) => setProvinces(r.data));
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const currentProv = provinces.find((p) => p.name === form.province);

  const submit = async () => {
    if (!form.service_type || !form.province || !form.port) {
      toast.error("Rellena servicio, provincia y puerto"); return;
    }
    try {
      const { data } = await api.post("/requests", { ...form, preferred_date: form.preferred_date || null });
      toast.success("Solicitud publicada — los profesionales de tu zona ya la están viendo");
      setOpen(false);
      setForm({ service_type: "", boat_name: "", boat_size: "8-12m", province: "", comarca: "", port: "", preferred_date: "", notes: "", urgent: false, origin_port: "", origin_province: "", destination_port: "", destination_province: "" });
      nav(`/request/${data.id}`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="min-h-screen hub-cream">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div className="flex items-start gap-4">
            <label className="relative cursor-pointer group" data-testid="owner-photo-label">
              {user?.photo_path ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${user.photo_path}`} alt={user.name} className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-md"/>
              ) : (
                <div className="w-20 h-20 rounded-full bg-[#EFE9E1] flex items-center justify-center font-serif text-3xl text-[#0B1D3A] border-4 border-white shadow-md">
                  {user?.name?.[0] || "?"}
                </div>
              )}
              <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-[10px] uppercase tracking-widest">
                Cambiar
              </div>
              <input type="file" accept="image/*" className="hidden" data-testid="owner-photo-input"
                onChange={async (e) => {
                  const file = e.target.files?.[0]; if (!file) return;
                  if (file.size > 10*1024*1024) { toast.error("Máx 10 MB"); return; }
                  const fd = new FormData(); fd.append("file", file);
                  try {
                    await api.post("/users/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    toast.success("Foto actualizada");
                    await (refresh && refresh());
                  } catch (err) { toast.error(apiErr(err)); }
                }}/>
            </label>
            <div>
              <div className="hub-pill hub-sand text-[#0B1D3A] mb-3"><Ship size={12}/> Panel del propietario</div>
              <h1 className="font-serif text-5xl text-[#0B1D3A] tracking-tight">{t("dash.owner.hello")}, {user?.name?.split(" ")[0] || "capitán"}.</h1>
              <p className="text-[#5C6B7D] mt-2">{t("dash.owner.desc")}</p>
            </div>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="hub-btn-primary gap-2" data-testid="new-request-btn"><Plus size={16}/> {t("dash.owner.new")}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-serif text-3xl">Cuéntanos qué necesitas</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-2">
                {boats.length > 0 && (
                  <div>
                    <Label className="text-xs uppercase tracking-widest font-display">Barco</Label>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {boats.map(b => {
                        const active = form.boat_name === b.name && form.port === b.home_port;
                        return (
                          <button key={b.id} type="button"
                            onClick={() => setForm({ ...form, boat_name: b.name, boat_size: b.size || "8-12m", port: b.home_port || "", province: b.home_province || "", comarca: "" })}
                            data-testid={`nr-boat-${b.id}`}
                            className={`p-3 rounded-lg border text-left text-sm transition-colors ${active ? "border-[#FF6B57] bg-[#FF6B57]/5" : "border-[#0B1D3A]/15 bg-white"}`}>
                            <div className="flex items-center gap-2"><Ship size={14} className="hub-coral"/><span className="font-display font-semibold">{b.name}</span></div>
                            <div className="text-xs text-[#5C6B7D] mt-1">{b.size}{b.home_port ? ` · ${b.home_port}` : " · sin puerto base"}</div>
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-[#5C6B7D] italic mt-1">Rellena automáticamente el puerto y la provincia. Puedes cambiarlos abajo.</p>
                  </div>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-widest font-display">Servicio</Label>
                  <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2 max-h-56 overflow-y-auto">
                    {services.map((s) => (
                      <button key={s.id} type="button" data-testid={`nr-svc-${s.id}`}
                        onClick={() => setForm({ ...form, service_type: s.id })}
                        className={`p-3 rounded-lg border text-left text-sm transition-colors ${form.service_type === s.id ? "border-[#FF6B57] bg-[#FF6B57]/5" : "border-[#0B1D3A]/15 bg-white"}`}>
                        {s.name}
                      </button>
                    ))}
                  </div>
                  {priceHint && (
                    <div className="mt-2 p-3 rounded-lg bg-[#FF6B57]/5 border border-[#FF6B57]/20 flex items-center justify-between flex-wrap gap-2" data-testid="price-hint">
                      <div className="text-xs text-[#5C6B7D]">
                        💡 Rango habitual{form.port ? ` en ${form.port}` : ""}: <b className="text-[#0B1D3A]">{priceHint.min}€ – {priceHint.max}€</b>
                        {priceHint.sample_size > 0 && <span className="text-[10px] ml-1">({priceHint.sample_size} ofertas reales)</span>}
                        {priceHint.fallback && <span className="text-[10px] ml-1 italic">(orientativo)</span>}
                      </div>
                      <div className="text-lg font-serif text-[#FF6B57]">~{priceHint.median}€</div>
                    </div>
                  )}
                </div>
                {form.service_type === "traslados" && (
                  <div className="hub-card p-4 bg-[#FBF9F5] space-y-3" data-testid="traslado-fields">
                    <Label className="text-xs uppercase tracking-widest font-display">Origen y destino del traslado</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#5C6B7D] mb-1">Puerto de origen</div>
                        <Input value={form.origin_port} onChange={e => setForm({ ...form, origin_port: e.target.value })}
                          placeholder="Ej: Puerto de Palma" data-testid="nr-origin"/>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#5C6B7D] mb-1">Puerto de destino</div>
                        <Input value={form.destination_port} onChange={e => setForm({ ...form, destination_port: e.target.value })}
                          placeholder="Ej: Marina Alicante" data-testid="nr-dest"/>
                      </div>
                    </div>
                    <p className="text-[10px] text-[#5C6B7D] italic">Los traslados suelen incluir combustible aparte. Coordina detalles en el chat con el pro.</p>
                  </div>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-widest font-display flex items-center gap-2"><MapIcon size={12}/> Elige tu puerto en el mapa</Label>
                  <div className="mt-2">
                    <PortsMap height={280} selected={{ name: form.port }} onSelect={(p) => setForm({ ...form, port: p.name, province: p.province, comarca: "" })}/>
                  </div>
                  {form.port && <div className="mt-2 text-sm hub-coral font-display">📍 {form.port} · {form.province}</div>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest font-display">Nombre del barco</Label>
                    <Input value={form.boat_name} onChange={(e) => setForm({ ...form, boat_name: e.target.value })} placeholder="Opcional" className="mt-1.5" data-testid="nr-boat-name"/>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest font-display">Eslora</Label>
                    <Select value={form.boat_size} onValueChange={(v) => setForm({ ...form, boat_size: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="nr-boat-size"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        {["< 8m", "8-12m", "12-18m", "> 18m"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest font-display">Provincia</Label>
                    <Select value={form.province} onValueChange={(v) => setForm({ ...form, province: v, comarca: "", port: "" })}>
                      <SelectTrigger className="mt-1.5" data-testid="nr-province"><SelectValue placeholder="Elige"/></SelectTrigger>
                      <SelectContent>
                        {provinces.map((p) => <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest font-display">Comarca</Label>
                    <Select value={form.comarca} onValueChange={(v) => setForm({ ...form, comarca: v })} disabled={!currentProv}>
                      <SelectTrigger className="mt-1.5" data-testid="nr-comarca"><SelectValue placeholder="Opcional"/></SelectTrigger>
                      <SelectContent>
                        {currentProv?.comarcas.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest font-display">Puerto</Label>
                    <Select value={form.port} onValueChange={(v) => setForm({ ...form, port: v })} disabled={!currentProv}>
                      <SelectTrigger className="mt-1.5" data-testid="nr-port"><SelectValue placeholder="Elige"/></SelectTrigger>
                      <SelectContent>
                        {currentProv?.ports.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest font-display">Fecha preferida</Label>
                  <Input type="date" value={form.preferred_date || ""} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} className="mt-1.5" data-testid="nr-date"/>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest font-display">Notas</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Cuéntanos detalles del barco y del servicio" className="mt-1.5" data-testid="nr-notes"/>
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox id="urgent" checked={form.urgent} onCheckedChange={(v) => setForm({ ...form, urgent: !!v })} data-testid="nr-urgent"/>
                  <Label htmlFor="urgent" className="text-sm flex items-center gap-2"><Zap size={14} className="hub-coral"/> Marcar como urgente</Label>
                </div>
                <Button onClick={submit} className="hub-btn-primary w-full h-11" data-testid="nr-submit">Publicar solicitud</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid md:grid-cols-12 gap-6">
          <div className="md:col-span-8 space-y-4">
            <h2 className="font-serif text-2xl text-[#0B1D3A]">{t("dash.owner.yours")}</h2>
            {reqs.length === 0 && (
              <div className="hub-card p-10 text-center">
                <Anchor className="mx-auto mb-3 text-[#5C6B7D]"/>
                <p className="text-[#5C6B7D]">Aún no has publicado nada. Empieza con tu primera solicitud.</p>
              </div>
            )}
            {reqs.map((r) => {
              const s = services.find(x => x.id === r.service_type);
              const st = STATUS_LABEL[r.status] || STATUS_LABEL.open;
              return (
                <button key={r.id} onClick={() => nav(`/request/${r.id}`)} data-testid={`req-card-${r.id}`}
                  className="hub-card p-6 w-full text-left flex items-center gap-5 group">
                  <div className="w-14 h-14 rounded-xl hub-sand flex items-center justify-center shrink-0"><Ship className="text-[#0B1D3A]"/></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`hub-pill ${st.cls}`}>{st.text}</span>
                      {r.urgent && <span className="hub-pill bg-[#FF6B57]/10 text-[#FF6B57]"><Zap size={11}/> Urgente</span>}
                    </div>
                    <div className="font-serif text-xl mt-2">{s?.name || r.service_type}{r.boat_name ? ` · ${r.boat_name}` : ""}</div>
                    <div className="text-sm text-[#5C6B7D] mt-1 flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1"><MapPin size={12}/> {r.port}, {r.province}</span>
                      <span>·</span>
                      <span>{r.boat_size}</span>
                      <span>·</span>
                      <span className="font-semibold text-[#FF6B57]">{r.offers_count || 0} oferta{r.offers_count === 1 ? "" : "s"}</span>
                    </div>
                  </div>
                  <ChevronRight className="text-[#5C6B7D] group-hover:translate-x-1 transition-transform"/>
                </button>
              );
            })}
          </div>
          <aside className="md:col-span-4 space-y-6">
            {nearby.length > 0 && (
              <div>
                <h2 className="font-serif text-2xl text-[#0B1D3A] mb-3 flex items-center gap-2"><Star className="hub-coral fill-[#FF6B57]" size={18}/> Profesionales cerca de tus barcos</h2>
                <div className="space-y-2">
                  {nearby.slice(0, 4).map((p) => (
                    <button key={p.id} onClick={() => nav(`/pro/profile/${p.id}`)} className="hub-card p-3 w-full text-left flex items-center gap-3" data-testid={`nearby-pro-${p.id}`}>
                      <div className="w-10 h-10 rounded-full hub-sand flex items-center justify-center font-serif shrink-0">{p.name[0]}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-semibold text-sm truncate">{p.name}</div>
                        <div className="text-xs text-[#5C6B7D] flex items-center gap-1"><Star size={11} className="fill-[#FF6B57] text-[#FF6B57]"/> {(p.professional?.rating || 0).toFixed(1)} · {p.professional?.jobs_completed || 0}</div>
                      </div>
                      <span className="hub-pill bg-[#FF6B57] text-white text-[9px]">Destacado</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <h2 className="font-serif text-2xl text-[#0B1D3A] mb-4">Novedades</h2>
              <div className="space-y-3">
                {notifs.length === 0 && <div className="hub-card p-5 text-sm text-[#5C6B7D]">Todo tranquilo. Cuando llegue una oferta, aparecerá aquí.</div>}
                {notifs.map((n, i) => (
                  <button key={n.offer?.id || n.request?.id || i} onClick={() => nav(`/request/${n.request?.id}`)} className="hub-card p-4 w-full text-left" data-testid={`notif-${n.offer?.id || i}`}>
                    <div className="text-xs uppercase tracking-widest text-[#FF6B57] font-display">Nueva oferta</div>
                    <div className="font-display font-semibold mt-1">{n.offer?.professional_name}</div>
                    <div className="text-sm text-[#5C6B7D]">{n.offer?.price}€ · {n.request?.port}</div>
                    <div className="flex items-center gap-1 text-xs text-[#5C6B7D] mt-2"><Star size={11} className="fill-[#FF6B57] text-[#FF6B57]"/> {n.offer?.professional_rating?.toFixed(1) || "—"}</div>
                  </button>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
